import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { Auth } from '@/pages/Auth'
import { Onboarding } from '@/pages/Onboarding'
import { Home } from '@/pages/Home'
import { Practice } from '@/pages/Practice'
import { Session } from '@/pages/Session'
import { History } from '@/pages/History'
import { Settings } from '@/pages/Settings'
import { RoadmapReady } from '@/pages/RoadmapReady'
import { VisualToolsTest } from '@/pages/VisualToolsTest'
import { CurriculumTest } from '@/pages/CurriculumTest'
import { SessionTest } from '@/pages/SessionTest'
import { ToastProvider } from '@/components/ui/toast-provider'
import { Sentry } from '@/lib/sentry'

function AuthLoadingState() {
  return (
    <div className="h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-stone-500">Loading...</p>
      </div>
    </div>
  )
}

function AuthErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4 text-center px-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-amber-600" />
        </div>
        <p className="text-stone-700">Connection issue</p>
        <p className="text-sm text-stone-500">Please check your internet and try again</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
        >
          Retry
        </button>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, needsOnboarding, authError, retryAuth } = useAuth()

  console.log('ProtectedRoute:', { isLoading, isAuthenticated, needsOnboarding, authError })

  // Loading state - show spinner
  if (isLoading) {
    return <AuthLoadingState />
  }

  // Error state - show retry option (token might still be valid)
  if (authError) {
    return <AuthErrorState onRetry={retryAuth} />
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  // Skip loading state - let children render immediately
  // Auth page handles its own loading state internally
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, needsOnboarding, authError, retryAuth } = useAuth()

  // Loading state - show spinner
  if (isLoading) {
    return <AuthLoadingState />
  }

  // Error state - show retry option
  if (authError) {
    return <AuthErrorState onRetry={retryAuth} />
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />
  }

  if (!needsOnboarding) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
        <p className="text-gray-600 mb-6">We've been notified and are working on it.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh page
        </button>
      </div>
    </div>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <ToastProvider />
        <Routes>
        <Route
          path="/auth"
          element={
            <AuthRoute>
              <Auth />
            </AuthRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <Onboarding />
            </OnboardingRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/practice"
          element={
            <ProtectedRoute>
              <Practice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session/:id"
          element={
            <ProtectedRoute>
              <Session />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roadmap-ready"
          element={
            <ProtectedRoute>
              <RoadmapReady />
            </ProtectedRoute>
          }
        />
        {/* Test routes - no auth required */}
        <Route path="/test/visual-tools" element={<VisualToolsTest />} />
        <Route path="/test/curriculum" element={<CurriculumTest />} />
        <Route path="/test/session" element={<SessionTest />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}

export default App
